import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { SortHeader } from '@/components/ui/sort-header'
import { Download, RefreshCw, TrendingDown, MessageCircle, Mail, Loader2, FileText, User } from 'lucide-react'
import { useDeudores } from '@/hooks/useReportes'
import { reportesService } from '@/services/reportesService'
import { formatCurrency, formatDate, generarEnlaceWhatsApp } from '@/lib/utils'
import { emailService } from '@/services/emailService'
import { useAuth0 } from '@auth0/auth0-react'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import { usePermissions } from '@/hooks/usePermissions'
import { EstadoCuentaDialog } from '@/components/clientes/EstadoCuentaDialog'
import { EnvioMasivoEstadoCuentaDialog } from '@/components/clientes/EnvioMasivoEstadoCuentaDialog'
import { claseBadgeDiasSinPago } from './antiguedad-utils'
import type { VistaSaldoCliente } from '@/types/database'
import { rutaFichaCliente } from '@/lib/ruta-cliente'
import type { VistaSaldoClienteConImpuestos } from '@/services/clientesService'
import { toast } from 'sonner'

type SortField = 'nombre' | 'responsable' | 'saldo' | 'ultimoPago' | 'diasSinPago'
type SortDirection = 'asc' | 'desc'

/**
 * Tabla de todos los deudores de la organización, con pestañas de activos y dados de baja.
 * Trae las acciones de cobranza: WhatsApp, recordatorio por email, estado de cuenta y envío masivo.
 * El recordatorio por email se manda solo después de confirmarlo en un diálogo.
 * Exportar CSV exige exportarReportes y el envío masivo exige editarCliente.
 */
export function TablaDeudores() {
  const [filtroMinDeuda, setFiltroMinDeuda] = useState<string>('')
  const [tabEstado, setTabEstado] = useState<'activo' | 'inactivo'>('activo')
  const [enviandoEmail, setEnviandoEmail] = useState<string | null>(null)
  const [deudorRecordatorio, setDeudorRecordatorio] = useState<(typeof deudores)[number] | null>(null)
  const [clienteEstadoCuenta, setClienteEstadoCuenta] = useState<VistaSaldoClienteConImpuestos | null>(null)
  const [envioMasivoOpen, setEnvioMasivoOpen] = useState(false)
  const [sortField, setSortField] = useState<SortField>('saldo')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const navigate = useNavigate()
  const { data: configEstudio } = useConfiguracionEstudio()
  const { puede } = usePermissions()

  const {
    data: deudores = [],
    isLoading,
    refetch,
  } = useDeudores({
    minDeuda: filtroMinDeuda ? parseFloat(filtroMinDeuda) : undefined,
    estado: tabEstado,
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedDeudores = useMemo(() => {
    const factor = sortDirection === 'asc' ? 1 : -1
    return [...deudores].sort((a, b) => {
      switch (sortField) {
        case 'nombre':
          return factor * (a.nombre ?? '').localeCompare(b.nombre ?? '')
        case 'responsable':
          return factor * (a.responsable ?? '').localeCompare(b.responsable ?? '')
        case 'saldo':
          return factor * ((a.saldo ?? 0) - (b.saldo ?? 0))
        case 'ultimoPago': {
          // "Sin pagos" siempre al final, sin importar la dirección
          const ta = a.ultimoPago ? new Date(a.ultimoPago).getTime() : null
          const tb = b.ultimoPago ? new Date(b.ultimoPago).getTime() : null
          if (ta === null && tb === null) return 0
          if (ta === null) return 1
          if (tb === null) return -1
          return factor * (ta - tb)
        }
        case 'diasSinPago': {
          // Sin dato ("-") siempre al final, sin importar la dirección
          const da = a.diasSinPago ?? null
          const db = b.diasSinPago ?? null
          if (da === null && db === null) return 0
          if (da === null) return 1
          if (db === null) return -1
          return factor * (da - db)
        }
        default:
          return 0
      }
    })
  }, [deudores, sortField, sortDirection])

  const handleExportar = () => {
    const csv = reportesService.generarCSVDeudas(deudores)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `reporte-deudas-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Reporte exportado correctamente')
  }

  const handleEnviarRecordatorio = async (deudor: typeof deudores[0]) => {
    if (!deudor.email) {
      toast.error('El cliente no tiene email configurado')
      return
    }
    if (!organizacionId) {
      toast.error('No se pudo identificar la organización. Recargá la página.')
      return
    }

    setEnviandoEmail(deudor.id)
    try {
      const token = await getAccessTokenSilently()
      const result = await emailService.enviarRecordatorioDeuda(
        token,
        { id: deudor.id ?? undefined, nombre: deudor.nombre ?? '', email: deudor.email ?? '' },
        deudor.saldo || 0,
        deudor.diasSinPago,
        organizacionId,
        configEstudio?.telefono || undefined
      )

      if (result.success) {
        toast.success(`Recordatorio enviado a ${deudor.email}`)
      } else {
        toast.error(result.error || 'Error al enviar el email')
      }
    } catch (err) {
      toast.error('Error al enviar el email')
      console.error(err)
    } finally {
      setEnviandoEmail(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Tabs Activos/Inactivos */}
      <Tabs value={tabEstado} onValueChange={(v) => setTabEstado(v as 'activo' | 'inactivo')}>
        <TabsList>
          <TabsTrigger value="activo">Clientes Activos</TabsTrigger>
          <TabsTrigger value="inactivo">Dados de Baja</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filtros y acciones */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-4 items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground whitespace-nowrap">
              Deuda mínima:
            </label>
            <Input
              type="number"
              placeholder="0"
              value={filtroMinDeuda}
              onChange={(e) => setFiltroMinDeuda(e.target.value)}
              className="w-32"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {puede.exportarReportes && (
            <Button variant="outline" onClick={handleExportar} disabled={deudores.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
          )}
          {/* Comunicación masiva con clientes: gateada por editarCliente (ORR-143).
              Restaura el gate que tenían en Clientes antes de mover los botones a Reportes. */}
          {puede.editarCliente && (
            <Button
              onClick={() => setEnvioMasivoOpen(true)}
              disabled={!deudores.some(d => (d.saldo ?? 0) > 0 && d.email)}
              title="Enviar estado de cuenta a los deudores con email"
            >
              <FileText className="h-4 w-4 mr-2" />
              Estados de cuenta
            </Button>
          )}
        </div>
      </div>

      {/* Tabla de deudores */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortHeader onClick={() => handleSort('nombre')} active={sortField === 'nombre'} direction={sortDirection}>
                  Cliente
                </SortHeader>
              </TableHead>
              <TableHead>
                <SortHeader onClick={() => handleSort('responsable')} active={sortField === 'responsable'} direction={sortDirection}>
                  Responsable
                </SortHeader>
              </TableHead>
              <TableHead>Contacto</TableHead>
              <TableHead className="text-right">
                <SortHeader onClick={() => handleSort('saldo')} active={sortField === 'saldo'} direction={sortDirection}>
                  Saldo
                </SortHeader>
              </TableHead>
              <TableHead>
                <SortHeader onClick={() => handleSort('ultimoPago')} active={sortField === 'ultimoPago'} direction={sortDirection}>
                  Último Pago
                </SortHeader>
              </TableHead>
              <TableHead>
                <SortHeader onClick={() => handleSort('diasSinPago')} active={sortField === 'diasSinPago'} direction={sortDirection}>
                  Días sin Pago
                </SortHeader>
              </TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-28 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-5 w-16 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell />
                </TableRow>
              ))
            ) : deudores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No hay clientes con deuda pendiente
                </TableCell>
              </TableRow>
            ) : (
              sortedDeudores.map((deudor) => (
                <TableRow
                  key={deudor.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setClienteEstadoCuenta(deudor as unknown as VistaSaldoClienteConImpuestos)}
                >
                  <TableCell className="font-medium">{deudor.nombre}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {deudor.responsable || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm flex items-center gap-2">
                      <div>
                        {deudor.email && <p>{deudor.email}</p>}
                        {deudor.telefono && (
                          <p className="text-muted-foreground">{deudor.telefono}</p>
                        )}
                      </div>
                      {deudor.telefono && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={(e) => {
                            e.stopPropagation()
                            const mensaje = `Hola ${deudor.nombre}, te contactamos desde el estudio contable respecto a tu saldo pendiente de ${formatCurrency(deudor.saldo || 0)}.`
                            const url = generarEnlaceWhatsApp(deudor.telefono, mensaje)
                            if (url) window.open(url, '_blank')
                          }}
                          title="Contactar por WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      )}
                      {deudor.email && puede.enviarEstadoCuenta && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeudorRecordatorio(deudor)
                          }}
                          disabled={enviandoEmail === deudor.id}
                          title="Enviar recordatorio por email"
                        >
                          {enviandoEmail === deudor.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-semibold text-red-600">
                      {formatCurrency(deudor.saldo || 0)}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {deudor.ultimoPago ? formatDate(deudor.ultimoPago) : 'Sin pagos'}
                  </TableCell>
                  <TableCell>
                    {deudor.diasSinPago !== undefined ? (
                      <Badge variant="secondary" className={claseBadgeDiasSinPago(deudor.diasSinPago)}>
                        {deudor.diasSinPago} días
                      </Badge>
                    ) : (
                      <Badge variant="secondary">-</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(deudor.id ? rutaFichaCliente(deudor.id, 'cuenta') : '/clientes')
                        }}
                        title="Ver ficha del cliente"
                      >
                        <User className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation()
                          setClienteEstadoCuenta(deudor as unknown as VistaSaldoClienteConImpuestos)
                        }}
                        title="Ver estado de cuenta"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Resumen inferior */}
      {!isLoading && deudores.length > 0 && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-red-500" />
          Mostrando {deudores.length} cliente(s) {tabEstado === 'activo' ? 'activos' : 'inactivos'} con deuda pendiente
        </div>
      )}

      <AlertDialog open={!!deudorRecordatorio} onOpenChange={(open) => !open && setDeudorRecordatorio(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar recordatorio de deuda?</AlertDialogTitle>
            <AlertDialogDescription>
              Se le enviará un recordatorio de deuda por {formatCurrency(deudorRecordatorio?.saldo || 0)} a{' '}
              {deudorRecordatorio?.nombre} ({deudorRecordatorio?.email}). ¿Querés continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deudorRecordatorio) handleEnviarRecordatorio(deudorRecordatorio)
                setDeudorRecordatorio(null)
              }}
            >
              Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Estado de Cuenta */}
      <EstadoCuentaDialog
        open={!!clienteEstadoCuenta}
        onOpenChange={(open) => !open && setClienteEstadoCuenta(null)}
        cliente={clienteEstadoCuenta}
      />

      {/* Envío masivo de estados de cuenta (movido desde Clientes — feedback Lisandro) */}
      <EnvioMasivoEstadoCuentaDialog
        open={envioMasivoOpen}
        onOpenChange={setEnvioMasivoOpen}
        clientes={deudores as VistaSaldoCliente[]}
      />
    </div>
  )
}
